import { BookOpen, Check, ChevronDown, Clock3, RefreshCw } from 'lucide-react'

import Button from '@/components/UiElements/Button'

import { OnboardingStep } from '../types'

type OnboardingStepCardProps = {
  step: OnboardingStep
  status: boolean | null
  isCurrent: boolean
  isExpanded: boolean
  isLoading: boolean
  onToggle: () => void
  onAction: () => void
  onOpenGuide: () => void
}

const OnboardingStepCard = ({
  step,
  status,
  isCurrent,
  isExpanded,
  isLoading,
  onToggle,
  onAction,
  onOpenGuide,
}: OnboardingStepCardProps) => {
  const isDone = status === true
  const isChecking = status === null && isLoading

  return (
    <article
      className={`overflow-hidden transition-colors ${
        isExpanded ? 'bg-gray-50' : isCurrent ? 'bg-gray-25' : 'bg-transparent'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex w-full items-center gap-md px-sm py-md text-left transition-colors hover:bg-gray-100"
      >
        <span
          className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-full border border-gray-100 bg-gray-100 text-text-xs font-semibold text-gray-900"
        >
          {isDone && <Check aria-hidden="true" className="h-[15px] w-[15px]" />}
        </span>

        <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
          <span className="flex flex-wrap items-center gap-sm">
            <span className={`text-title-md ${isDone ? 'text-gray-500' : 'text-gray-900'}`}>{step.title}</span>
            {isCurrent && !isDone && (
              <span className="rounded-full bg-primary-100 px-sm py-[2px] text-text-xs font-semibold text-primary-700">Next step</span>
            )}
          </span>
          <span className="flex items-center gap-xs text-text-sm text-gray-400">
            <Clock3 aria-hidden="true" className="h-[14px] w-[14px]" />
            About {step.estimatedMinutes} min
          </span>
        </span>

        <span className="flex shrink-0 items-center gap-sm">
          {!isDone && (
            <span className="text-text-sm font-medium text-gray-400">
              {isChecking ? 'Checking' : 'Not started'}
            </span>
          )}
          {isChecking && <RefreshCw aria-hidden="true" className="h-[15px] w-[15px] animate-spin text-gray-400" />}
          <ChevronDown
            aria-hidden="true"
            className={`h-[18px] w-[18px] text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      {isExpanded && (
        <div className="pb-lg pr-sm pl-[56px]">
          <p className="max-w-[760px] text-text-md leading-relaxed text-gray-500">{step.description}</p>
          <div className="mt-lg flex flex-wrap items-center gap-md">
            <Button onClick={onAction} icon="" iconPosition="right">
              {step.actionLabel}
            </Button>
            <Button type="link" onClick={onOpenGuide} icon={<BookOpen aria-hidden="true" className="h-[16px] w-[16px]" />}>
              View guide
            </Button>
          </div>
        </div>
      )}
    </article>
  )
}

export default OnboardingStepCard
